import { account } from '../core/account.js';
import { t } from '../core/i18n.js';
import { notify } from '../core/notify.js';
import { escapeHtml, formatBytes, formatDate, formatClock } from '../core/utils.js';
import { pageHead, empty } from '../ui/render.js';

/** «45 s» o «12 min»: lo grabado y lo que se esperaba. */
const spanText = (sec) => (sec >= 60 ? Math.round(sec / 60) + ' min' : Math.max(0, Math.round(sec)) + ' s');

/* Grabaciones del usuario: programadas, en curso y listas. Se refresca sola mientras haya alguna
   activa. Reproducir abre el reproductor con la URL de la grabación (local o S3 firmada). */

const STATUS_ICON = { scheduled: 'schedule', recording: 'fiber_manual_record', done: 'check_circle', failed: 'error' };

const KNOWN_FAIL = ['provider_busy', 'provider_denied', 'provider_gone', 'provider_error', 'stalled', 'bad_url', 'no_space', 'too_many_concurrent', 'missed'];
/** Por qué falló, en palabras: los motivos conocidos tienen texto; los demás enseñan lo que dijo ffmpeg. */
function failWhy(r) {
    if (KNOWN_FAIL.indexOf(r.error) !== -1) return t('rec.error.' + r.error, r.busy || { active: '?', max: '?' });
    return r.errorDetail || r.error;
}

function rowHtml(r) {
    const when = `${formatDate(r.startAt)} · ${formatClock(r.startAt)}–${formatClock(r.endAt)}`;
    const extra = r.status === 'done' ? `${formatBytes(r.size)} · ${t(r.storage === 's3' ? 'rec.storageS3' : 'rec.storageLocal')}${r.error === 'incomplete' ? ' · ' + (r.expectedSec ? t('rec.incompleteOf', { got: spanText(r.recordedSec), want: spanText(r.expectedSec) }) : t('rec.incomplete')) : ''}` : r.status === 'failed' ? (failWhy(r) !== 'rec.error.' + r.error ? failWhy(r) : r.error) : '';
    return `<div class="setting rec-row is-${r.status}" data-rec="${escapeHtml(r.id)}">
        <span class="mi rec-icon">${STATUS_ICON[r.status] || 'movie'}</span>
        <span class="setting-body">
            <span class="setting-title">${escapeHtml(r.title)}</span>
            <span class="setting-sub">${escapeHtml(r.channelName)} · ${when}</span>
            <span class="setting-sub">${t('rec.status.' + r.status)}${extra ? ' · ' + escapeHtml(extra) : ''}${r.status === 'recording' && r.waiting === 'provider_busy' ? ' · ' + escapeHtml(t('rec.waitingConn', r.busy || { active: '?', max: '?' })) : ''}</span>
        </span>
        <span class="rec-actions">
            ${r.status === 'done' ? `<button type="button" class="btn btn-sm btn-primary" data-rec-play="${escapeHtml(r.id)}" data-title="${escapeHtml(r.title)}"><span class="mi">play_arrow</span><span class="rec-label">${t('rec.play')}</span></button>` : ''}
            ${r.status === 'recording' ? (r.stopping ? `<span class="rec-stopping">${t('rec.stopping')}</span>` : `<button type="button" class="btn btn-sm btn-primary rec-stop" title="${t('rec.stop')}" aria-label="${t('rec.stop')}" data-rec-stop="${escapeHtml(r.id)}" data-title="${escapeHtml(r.title)}"><span class="mi">stop</span><span class="rec-label">${t('rec.stop')}</span></button>`) : ''}
            <button type="button" class="btn btn-icon" data-rec-delete="${escapeHtml(r.id)}" data-title="${escapeHtml(r.title)}" aria-label="${r.status === 'done' || r.status === 'failed' ? t('rec.delete') : t('rec.cancel')}"><span class="mi">${r.status === 'done' || r.status === 'failed' ? 'delete' : 'close'}</span></button>
        </span>
    </div>`;
}

export default {
    path: '/recordings/',
    nav: 'recordings',

    render() {
        return `<div class="page" data-name="recordings">
            <div class="page-content">
                ${pageHead({ title: t('rec.title') })}
                <div class="page-sub" data-usage></div>
                <div class="settings-group" data-list><div class="spinner"></div></div>
            </div>
        </div>`;
    },

    mount(page) {
        const root = page.el;
        const list = root.querySelector('[data-list]');
        const usageEl = root.querySelector('[data-usage]');
        let timer = null;
        let alive = true;

        const load = async () => {
            if (!alive) return;
            if (!account.canRecord) {
                list.innerHTML = empty({ icon: 'fiber_manual_record', title: t('rec.error.disabled') });
                return;
            }
            try {
                const r = await account.listRecordings();
                if (!alive) return;
                const items = r.recordings || [];
                usageEl.textContent = r.usage ? t('rec.usage', { used: formatBytes(r.usage.used), quota: formatBytes(r.usage.quota) }) : '';
                list.innerHTML = items.length ? items.map(rowHtml).join('') : empty({ icon: 'fiber_manual_record', title: t('rec.empty'), text: t('rec.emptyText') });
                clearInterval(timer);
                if (items.some((x) => x.status === 'scheduled' || x.status === 'recording')) timer = setInterval(load, items.some((x) => x.stopping) ? 3000 : 15000);
            } catch (e) {
                list.innerHTML = empty({ icon: 'cloud_off', title: t('common.errorNetwork') });
            }
        };

        root.addEventListener('click', (e) => {
            const play = e.target.closest('[data-rec-play]');
            if (play) {
                e.preventDefault();
                const id = play.getAttribute('data-rec-play');
                window.IPTV.player.open({ mode: 'vod', item: { id: 'rec:' + id, name: play.getAttribute('data-title'), url: account.recordingUrl(id), poster: '' }, startPosition: 0 });
                return;
            }
            const stop = e.target.closest('[data-rec-stop]');
            if (stop) {
                e.preventDefault();
                stop.disabled = true;
                account.stopRecording(stop.getAttribute('data-rec-stop')).then(() => {
                    notify.toast(t('rec.stopped', { title: stop.getAttribute('data-title') }), { icon: 'stop_circle', duration: 3500 });
                    load();
                }).catch(() => {
                    stop.disabled = false;
                    notify.error(t('common.errorNetwork'));
                });
                return;
            }
            const del = e.target.closest('[data-rec-delete]');
            if (del) {
                e.preventDefault();
                const id = del.getAttribute('data-rec-delete');
                notify.confirm(t('rec.deleteConfirm', { title: del.getAttribute('data-title') }), t('rec.delete'), async () => {
                    try {
                        await account.deleteRecording(id);
                        load();
                    } catch (err) {
                        notify.error(t('common.errorNetwork'));
                    }
                });
            }
        });

        load();
        return () => {
            alive = false;
            clearInterval(timer);
        };
    }
};
